import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";

type StripeError = {
	type: string;
	code?: string;
	decline_code?: string;
	message?: string;
};

type StripeCode =
	| "card_declined"
	| "generic_decline"
	| "insufficient_funds"
	| "lost_card"
	| "stolen_card"
	| "card_velocity_exceeded"
	| "expired_card"
	| "incorrect_cvc"
	| "incorrect_number"
	| "processing_error"
	| "invalid_postal_code"
	| "incomplete_number"
	| "invalid_expiry_month"
	| "invalid_expiry_year"
	| "invalid_expiry_year_past"
	| "invalid_cvc"
	| "invalid_number"
	| "incomplete_expiry"
	| "incomplete_cvc"
	| "incomplete_zip"
	| "incorrect_zip";

type AuthorizeNetCode =
	| "2"
	| "3"
	| "6"
	| "7"
	| "8"
	| "11"
	| "17"
	| "27"
	| "37"
	| "44"
	| "45"
	| "65"
	| "78"
	| "315"
	| "316"
	| "317";

const DEFAULT_PAYMENT_ERROR_MESSAGE = msg`There was an error while processing your payment. Please try again in a few minutes or contact your administrator for support`;

const STRIPE_PAYMENT_ERROR_MESSAGES: Record<StripeCode, MessageDescriptor> = {
	card_declined: msg`Your card was declined. Please try a different card or contact your bank.`,
	generic_decline: msg`Your card was declined. Please try a different card or contact your bank.`,
	insufficient_funds: msg`Your card has insufficient funds. Please try a different card.`,
	lost_card: msg`Your card has been reported lost. Please use a different card.`,
	stolen_card: msg`Your card has been reported stolen. Please use a different card.`,
	card_velocity_exceeded: msg`Your card was declined for making repeated attempts too frequently. Please wait a few minutes before trying again.`,
	expired_card: msg`Your card has expired. Please use a card with a valid expiration date.`,
	incorrect_cvc: msg`Your card's security code (CVV/CVC) is incorrect. Please check the 3-digit code on the back of your card.`,
	incorrect_number: msg`Your card number is invalid. Please check and re-enter your card number.`,
	processing_error: msg`An error occurred while processing your card. Please try again or use a different card.`,
	invalid_postal_code: msg`Your postal code is invalid. Please enter a valid postal code that matches your billing address.`,
	incomplete_number: msg`Your card number is incomplete. Please enter all digits of your card number.`,
	invalid_expiry_month: msg`The expiration month is invalid. Please enter a valid month (01-12).`,
	invalid_expiry_year: msg`The expiration year is invalid. Please enter a valid year.`,
	invalid_expiry_year_past: msg`The expiration year is in the past. Please enter a valid future expiration date.`,
	invalid_cvc: msg`Your card's security code (CVV/CVC) is invalid. Please check the 3-digit code on the back of your card.`,
	invalid_number: msg`Your card number is invalid. Please check and re-enter your card number.`,
	incomplete_expiry: msg`Your card's expiration date is incomplete. Please enter the full expiration date (MM/YY).`,
	incomplete_cvc: msg`Your card's security code is incomplete. Please enter the full CVV/CVC.`,
	incomplete_zip: msg`Your postal code is incomplete. Please enter your full postal code.`,
	incorrect_zip: msg`Your postal code does not match your billing address. Please verify and try again.`,
};

const AUTHORIZE_NET_PAYMENT_ERROR_MESSAGES: Record<AuthorizeNetCode, MessageDescriptor> = {
	"2": msg`Your card was declined. Please try a different card or contact your bank.`,
	"3": msg`Your card was declined. Please try a different card or contact your bank.`,
	"6": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"7": msg`The expiration date is invalid. Please enter a valid expiration date.`,
	"8": msg`Your card has expired. Please use a card with a valid expiration date.`,
	"11": msg`Your card was declined. Please try a different card or contact your bank.`,
	"17": msg`This card type is not accepted here. Please try a different card.`,
	"27": msg`Your postal code does not match your billing address. Please verify and try again.`,
	"37": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"44": msg`Your card's security code (CVV/CVC) was declined. Please check the 3-digit code on the back of your card.`,
	"45": msg`Your card's security code (CVV/CVC) was declined. Please check the 3-digit code on the back of your card.`,
	"65": msg`Your card's security code (CVV/CVC) was declined. Please check the 3-digit code on the back of your card.`,
	"78": msg`Your card's security code (CVV/CVC) is invalid. Please check the 3-digit code on the back of your card.`,
	"315": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"316": msg`The expiration date is invalid. Please enter a valid expiration date.`,
	"317": msg`Your card has expired. Please use a card with a valid expiration date.`,
};

type AuthorizeNetError = {
	message: string;
	authorizeNetDeclineCode?: string;
};

type PaymentErrorAlertProps = {
	error?: StripeError | AuthorizeNetError;
};

const isStripeError = (error: StripeError | AuthorizeNetError): error is StripeError => {
	return "type" in error;
};

const isStripeCode = (code: string | undefined): code is StripeCode => !!code && code in STRIPE_PAYMENT_ERROR_MESSAGES;
const isAuthorizeNetCode = (code: string | undefined): code is AuthorizeNetCode =>
	!!code && code in AUTHORIZE_NET_PAYMENT_ERROR_MESSAGES;

const getStripeCode = (error: StripeError): StripeCode | undefined => {
	if (isStripeCode(error.decline_code)) {
		return error.decline_code;
	}
	if (isStripeCode(error.code)) {
		return error.code;
	}
	return undefined;
};

export const PaymentErrorAlert = ({ error }: PaymentErrorAlertProps) => {
	const { i18n, t } = useLingui();
	const getMessage = () => {
		if (!error) {
			return i18n._(DEFAULT_PAYMENT_ERROR_MESSAGE);
		}
		if (isStripeError(error)) {
			if (!error.message) {
				return i18n._(DEFAULT_PAYMENT_ERROR_MESSAGE);
			}
			const code = getStripeCode(error);
			if (code) {
				return i18n._(STRIPE_PAYMENT_ERROR_MESSAGES[code]);
			}
			return i18n._(DEFAULT_PAYMENT_ERROR_MESSAGE);
		}
		if ("authorizeNetDeclineCode" in error) {
			if (isAuthorizeNetCode(error.authorizeNetDeclineCode)) {
				return i18n._(AUTHORIZE_NET_PAYMENT_ERROR_MESSAGES[error.authorizeNetDeclineCode]);
			}
			if (error.authorizeNetDeclineCode && error.message) {
				return error.message;
			}
			return i18n._(DEFAULT_PAYMENT_ERROR_MESSAGE);
		}
		return error.message || i18n._(DEFAULT_PAYMENT_ERROR_MESSAGE);
	};
	return <Alert type={"error"} title={t`Payment Failed`} message={getMessage()} />;
};
